import React, { FunctionComponent, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, TextInput } from 'shamrock-clover-ui';
import css from './styles/ourServices.scss';
import { RootState } from 'bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { User } from 'interfaces/user';
import { useState } from 'react';
import { validateEmail, debounce } from '@shamrock-core/common/utils';
import Fallback from '../core/fallback';
import { emailService, TemplateEmailBody } from '../../services/email.service';
import FirebaseAnalytics from './analytics';
import rtsproUserService from '../../services/rtsproUser.service';
import { ProductRequests } from '../../types';
import { setUser } from '../../redux/actions/appActions';
import {padDateString} from '../../utils/utils';
import { featureService } from '../../services/feature.service';
const Config = require('Config');

const ourServicesTemplateId = 'd-f9b1d9ba6d4243c196d6122d85bb8616';
const trialExtensionTemplateId = 'd-1e3568ef7b6e4bbd97a763c119c652cc';

interface OurServiceProps {
  serviceKey: string,
  headerText: string,
  bodyText: string,
  logFirebaseEvent: (module: string, page: string, description: string) => void,
}

interface FormInput {
  fullName: FormField,
  phone: FormField,
  companyName: FormField,
  mcNumber: FormField,
  email: FormField,
  comments: FormField,
}

interface FormField {
  value: string,
  required: boolean,
}

interface FormErrors {
  fullName: string,
  phone: string,
  companyName: string,
  mcNumber: string,
  email: string,
}

export const OurServices: FunctionComponent<OurServiceProps> = ({
  serviceKey, headerText, bodyText, logFirebaseEvent,
}) => {
  const dispatch = useDispatch();
  const user = useSelector<RootState, User>(state => state.app.user);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [submitStarted, setSubmitStarted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [formInput, setFormInput] = useState<FormInput>({
    fullName: {
      value: '',
      required: true,
    },
    phone: {
      value: '',
      required: true,
    },
    companyName: {
      value: '',
      required: true,
    },
    mcNumber: {
      value: '',
      required: false,
    },
    email: {
      value: user?.email || '',
      required: true,
    },
    comments: {
      value: '',
      required: false,
    },
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({
    fullName: '',
    phone: '',
    companyName: '',
    mcNumber: '',
    email: '',
  });

  useEffect(() => {
    const missingRequiredField = Object.values(formInput)
      .flatMap(value => value.required ? value.value : [])
      .some((value) => [null, ''].includes(value));
    const hasErrors = Object.values(formErrors).some(value => value !== '');

    setSubmitDisabled(missingRequiredField || hasErrors);
  }, [formErrors, formInput]);

  useEffect(() => {
    const sendLogEvent = () => logFirebaseEvent(
      FirebaseAnalytics.MODULES.OUR_SERVICES,
      FirebaseAnalytics.PAGES.OUR_SERVICES,
      `exit ${serviceKey}`,
    );

    window.addEventListener('popstate', sendLogEvent);
    return () => window.removeEventListener('popstate', sendLogEvent);
  }, []);

  const updateErrors = (fieldName: string, message: string) => {
    setFormErrors((prevFormErrors) => ({
      ...prevFormErrors,
      [fieldName]: message,
    }));
  };

  const displayPhoneNumber = () => {
    const phoneValue = formInput.phone.value;
    const phone = [
      phoneValue.substring(0, 3),
      phoneValue.substring(3, 6),
      phoneValue.substring(6, 10),
    ].flatMap(value => value ? value : []);

    return phone.length > 1 ?
      phone.reduce((prevValue: string, currValue: string, index) =>
        index === 1 ? `(${prevValue}) ${currValue}` : `${prevValue}-${currValue}`) :
      phone;
  };

  const validateInput = useCallback(debounce((fieldName: string, value: string) => {
    let errorMessage = '';

    switch (fieldName) {
      case 'fullName':
        if (value.length < 3) {
          errorMessage = 'Please enter a valid name';
        }
        break;

      case 'companyName':
        if (value.length < 3) {
          errorMessage = 'Please enter a valid company name';
        }
        break;

      case 'phone':
        if (value.charAt(0) === '1' || value.length < 10) {
          errorMessage = 'Invalid 10-digit Mobile Phone Number';
        }
        break;

      case 'email':
        if (!validateEmail(value)) {
          errorMessage = 'Invalid Email Address';
        }
        break;

      case 'mcNumber':
        if (value.length < 6 && value.length != 0) {
          errorMessage = 'Invalid MC or DOT Number';
        }
        break;

      // No need to update errors for something without validation
      default:
        return;
    }

    updateErrors(fieldName, errorMessage);
  }, 500), []);

  const updateInput = (event, fieldName: string) => {
    let value = event.target.value;
    if (fieldName === 'phone') {
      value = value.replace(/\D/g, '');
      value = value.substring(0, 10);
    }
    if (fieldName === 'mcNumber') {
      value = value.replace(/\D/g, '');
      value = value.substring(0, 8);
    }
    validateInput(fieldName, value);
    setFormInput((prevFormInput) => ({
      ...prevFormInput,
      [fieldName]: {
        ...prevFormInput[fieldName],
        value,
      },
    }));
  };

  const submit = async () => {
    const dateString = new Date(parseInt(user?.createdOn));
    const formattedCreatedOn = dateString ?
      `${padDateString(dateString.getMonth() + 1)}/${padDateString(dateString.getDate())}/${dateString.getFullYear()}` :
      '';
    const [sendgridTemplate, toEmail, requestedExtension] =
      serviceKey === 'Extend Trial' ? [
        trialExtensionTemplateId,
        Config.trialLeadEmail,
        true,
      ] : [
        ourServicesTemplateId,
        Config.supportEmail,
        false,
      ];
    setSubmitStarted(true);
    logFirebaseEvent(
      FirebaseAnalytics.LABELS.CONTACT_US,
      FirebaseAnalytics.PAGES.OUR_SERVICES,
      serviceKey,
    );
    const email: TemplateEmailBody = {
      to: toEmail,
      from: 'noreply@rtspro.com',
      cc: formInput.email.value,
      templateId: sendgridTemplate,
      dynamicTemplateData: {
        fullName: formInput.fullName.value,
        companyName: formInput.companyName.value,
        email: formInput.email.value,
        phoneNumber: formInput.phone.value,
        companyNumber: formInput.mcNumber.value,
        comments: formInput.comments.value,
        product: serviceKey,
        createdOn: formattedCreatedOn,
      },
    };
    try {
      await emailService.sendTemplateEmail(email);

      const updateTrialUserPayload: { trialExtensionRequested?: boolean; productRequest?: string } = {};
      if (requestedExtension) updateTrialUserPayload.trialExtensionRequested = requestedExtension;
      if (serviceKey !== 'Extend Trial') updateTrialUserPayload.productRequest = ProductRequests[serviceKey];

      const features = await featureService.getFeatures(user.email);
      const cpua5206 = features.includes('CPUA-5206');

      let updatedUser;
      if (cpua5206 && user.userStatus.isRegistered) {
        updatedUser = (await rtsproUserService.updateUser({ email: user.email }, updateTrialUserPayload))?.updateUser;
      } else {
        updatedUser = (await rtsproUserService.updateTrialUser(user.email, updateTrialUserPayload))?.updateTrialUser;
      }

      const newUser = { ...user };
      if (requestedExtension) {
        newUser.trialExtensionRequested = true;
      } else {
        newUser.productRequests = updatedUser.productRequests;
      }

      dispatch(setUser(newUser));
      setSubmitStarted(false);
      navigate(`${location.pathname}`, { state: null });
    } catch (error) {
      console.log('Failed to send email');
    }
  };

  return (
    <div className={css.wrapper}>
      {submitStarted && <Fallback />}
      <div className={css.inputWrapper}>
        <div>
          <p className={css.headerText}>{headerText}</p>
          <p className={css.bodyText}>{bodyText}</p>
        </div>
        <div className={css.inputRow}>
          <TextInput
            id="fullName"
            className={css.inputRowTextInput}
            label="Full Name*"
            value={formInput.fullName.value}
            error={formErrors.fullName}
            onChange={(event) => updateInput(event, 'fullName')}
          />
          <TextInput
            id="phone"
            className={css.inputRowTextInput}
            label="Phone*"
            value={displayPhoneNumber()}
            error={formErrors.phone}
            onChange={(event) => updateInput(event, 'phone')}
          />
        </div>
        <div className={css.inputRow}>
          <TextInput
            id="companyName"
            className={css.inputRowTextInput}
            label="Company Name*"
            value={formInput.companyName.value}
            error={formErrors.companyName}
            onChange={(event) => updateInput(event, 'companyName')}
          />
          <TextInput
            id="mcNumber"
            className={css.inputRowTextInput}
            label="DOT# or MC# (Optional)"
            maxLength={8}
            value={String(formInput.mcNumber.value)}
            error={formErrors.mcNumber}
            onChange={(event) => updateInput(event, 'mcNumber')}
          />
        </div>
        <TextInput
          id="email"
          className={css.textInput}
          label="Email*"
          value={formInput.email.value}
          error={formErrors.email}
          disabled={!!user?.email.length}
          onChange={(event) => updateInput(event, 'email')}
        />
        <TextInput
          id="comments"
          className={css.textInput}
          label="What would you like to tell us? (Optional)"
          value={formInput.comments.value}
          onChange={(event) => updateInput(event, 'comments')}
        />
      </div>
      <div className={css.submitButton}>
        <Button width='100%' disabled={submitDisabled || submitStarted} onClick={submit}>Submit</Button>
      </div>
    </div>
  );
};
